import { useCallback, useEffect, useRef } from 'react';
import { Avatar, Box, CircularProgress, IconButton, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { PARTNER_SWITCH_SX } from '../../partnerDashboardTheme';
import AttendeeEligibilityBadge from '../../../shared/registration/AttendeeEligibilityBadge';
import { PRESERVE_LINE_BREAKS_SX } from '../../../Events/ManageEvent/eventFormConstants';

const ActionIconButton = styled(IconButton)(({ theme, bordercolor, iconcolor, bgcolor }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${bordercolor ?? theme.palette.divider}`,
  color: iconcolor ?? theme.palette.text.secondary,
  backgroundColor: bgcolor ?? theme.palette.background.paper,
  flexShrink: 0,
  '&:hover': {
    backgroundColor: bgcolor ? bgcolor : theme.palette.action.hover,
  },
}));

const ErrorActionIconButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${theme.palette.error.light}`,
  color: theme.palette.error.main,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  '&:hover': {
    backgroundColor: theme.palette.error.light,
  },
}));

const sectionTitleSx = {
  fontWeight: 500,
  fontSize: '0.9375rem',
  color: 'text.secondary',
  mb: 1,
};

const ruleRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  py: 0.75,
};

const COMMUNITY_MEMBERS_LIST_HEIGHT = { xs: 280, sm: 320, md: 360 };
const MEMBERS_SCROLL_LOAD_THRESHOLD_PX = 56;

const CommunityDetailsPanel = ({
  description,
  requireJoinApproval,
  attendeeEligibility,
  members,
  memberCount = null,
  hasMoreMembers = false,
  loadingMoreMembers = false,
  readOnly = false,
  onRequireJoinApprovalChange,
  onViewProfile,
  onBlockMember,
  onLoadMoreMembers,
}) => {
  const loadMoreLockRef = useRef(false);
  const loadedCount = Array.isArray(members) ? members.length : 0;
  const totalCount =
    Number.isFinite(Number(memberCount)) && Number(memberCount) >= 0
      ? Number(memberCount)
      : loadedCount;

  useEffect(() => {
    if (!loadingMoreMembers) {
      loadMoreLockRef.current = false;
    }
  }, [loadingMoreMembers]);

  const handleMembersScroll = useCallback(
    (event) => {
      if (!hasMoreMembers || loadingMoreMembers || typeof onLoadMoreMembers !== 'function') {
        return;
      }
      const list = event.currentTarget;
      const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
      if (remaining > MEMBERS_SCROLL_LOAD_THRESHOLD_PX) {
        loadMoreLockRef.current = false;
        return;
      }
      if (loadMoreLockRef.current) {
        return;
      }
      loadMoreLockRef.current = true;
      onLoadMoreMembers();
    },
    [hasMoreMembers, loadingMoreMembers, onLoadMoreMembers],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(readOnly
          ? {
              opacity: 0.72,
              filter: 'grayscale(1)',
            }
          : {}),
      }}
    >
      <Typography sx={sectionTitleSx}>Description</Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.65,
          mb: 5,
          fontWeight: 700,
          ...PRESERVE_LINE_BREAKS_SX,
        }}
      >
        {description}
      </Typography>

      <Typography sx={{ ...sectionTitleSx, mb: 1.25 }}>Community Rules</Typography>
      <Box sx={{ mb: 2.5 }}>
        <Box sx={ruleRowSx}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Require Admin Approval
          </Typography>
          <Switch
            checked={requireJoinApproval}
            onChange={(event) => onRequireJoinApprovalChange?.(event.target.checked)}
            sx={PARTNER_SWITCH_SX}
            disabled
          />
        </Box>
        <Box sx={{ ...ruleRowSx, justifyContent: 'flex-start', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Attendee Eligibility
          </Typography>
          <AttendeeEligibilityBadge value={attendeeEligibility} variant="chip" />
        </Box>
      </Box>

      <Typography sx={{ ...sectionTitleSx, mb: 1.25, flexShrink: 0 }}>
        Members ({totalCount})
      </Typography>
      <Box
        onScroll={handleMembersScroll}
        sx={{
          height: COMMUNITY_MEMBERS_LIST_HEIGHT,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {loadedCount ? (
          <>
            {members.map((member) => (
              <Box
                key={member.id ?? member.uid}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  gap: 1.25,
                }}
              >
                <Avatar
                  src={member.avatarUrl}
                  alt={member.fullName}
                  sx={{ width: 40, height: 40 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 600,
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.fullName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  {!readOnly ? (
                    <>
                      <ActionIconButton
                        bordercolor="#E45E40"
                        iconcolor="#FFF"
                        bgcolor="#E45E40"
                        aria-label={`View ${member.fullName}`}
                        onClick={() => onViewProfile?.(member)}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                      </ActionIconButton>
                      <ErrorActionIconButton
                        aria-label={`Block ${member.fullName}`}
                        onClick={() => onBlockMember?.(member)}
                      >
                        <BlockOutlinedIcon sx={{ fontSize: 18 }} />
                      </ErrorActionIconButton>
                    </>
                  ) : null}
                </Box>
              </Box>
            ))}
            {loadingMoreMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            No members yet.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CommunityDetailsPanel;
